<template>
    <div class="document-allocations">
        <ag-grid-vue
            key="document-allocations"
            :style="`height: ${tableHeight}px;`"
            class="ag-theme-balham"
            @grid-ready="onGridReady"
            @cellValueChanged="onCellValueChanged"
            @cellClicked="onCellClicked"
            @click.navive.stop
            @selectionChanged="onSelectionChanged"
            domLayout="normal"
            :rowHeight="34"
            :rowBuffer="2"
            :getRowNodeId="getRowNodeId"
            :columnDefs="columnDefs"
            :defaultColDef="defaultColDef"
            :navigateToNextCell="navigateToNextCell"
            :frameworkComponents="frameworkComponents"
            :tooltipShowDelay="1500"
            :debounceVerticalScrollbar="true"
            :applyColumnDefOrder="true"
            :immutableData="true"
            :masterDetail="true"
            :enableFillHandle="true"
            :enableRangeSelection="true"
            :enableCellChangeFlash="!isSettingsEditMode"
            :suppressCellSelection="false"
            :stopEditingWhenGridLosesFocus="true"
            :suppressCopyRowsToClipboard="true"
            :processCellForClipboard="processCellForClipboard"
            :processCellFromClipboard="processCellFromClipboard"
            rowSelection="multiple">
        </ag-grid-vue>
        <div class="table-status-bar d-flex">
            <div class="flex-grow-1 text-left">
                Allocations: {{ allocations.length }}
            </div>
            <div class="flex-grow-1 text-right">
                <span v-tooltip="hasTotalAllocatedGreaterThanDocTotal && 'Total allocated greater than document total'"
                      :class="{'alert-danger': hasTotalAllocatedGreaterThanDocTotal}">
                    Allocated:
                    <span>{{ displayCurrency }} {{ formattedTotalAllocatedSum }}</span>
                </span>
                <span v-tooltip="hasTotalUnallocatedGreaterThanAllowed && 'Unallocated value is greater than allowed'"
                      :class="{'alert-danger': hasTotalUnallocatedGreaterThanAllowed}"
                      class="ml-3">
                    Unallocated:
                    <span>{{ displayCurrency }} {{ formattedTotalOutstandingSum }}</span>
                </span>
            </div>
        </div>

        <div v-if="!disabledDocument" class="row mx-0 mt-2">
            <div class="col-6 p-0 text-left">
                <button @click.prevent="onItemAdd"
                        :disabled="isLoading || isSettingsEditMode"
                        class="btn btn-sm border rounded clickable px-3 mr-2">
                    Add {{ itemTypeLabel }}
                </button>
            </div>
            <div class="col-6 text-right pr-0">
                <div v-if="selectedRows.length !== 0" class="d-inline-block border rounded px-2" style="height: 31px">
                    ({{ selectedRows.length }}) Selected {{ 'row' | pluralize(selectedRows.length) }}:
                    <button @click="onRemoveRows"
                            v-tooltip="'Delete selected rows'"
                            class="btn btn-sm text-danger">
                        <i class="fal fa-trash-alt fa-lg"></i>
                    </button>
                </div>
            </div>
        </div>
        <DocumentMatchingItemsSelectModal name="document-matching-items"/>
    </div>
</template>

<script>
import _ from 'lodash'
import accounting from 'accounting'
import {mapActions, mapGetters, mapMutations} from 'vuex'
import AgGridCellTooltip from '../lines/ag-grid/tooltip/AgGridCellTooltip'
import {DOC_FIELD_UPDATE, DOC_SET_ALLOCATIONS} from '@/store/modules/doc-viewer'
import {FormSystemSections} from '@/types/document-viewer/section'
import DocumentMatchingItemsSelectModal from '../matching/v2/DocumentMatchingItemsSelectModal'
import {forceToNumber} from '@/utils'
import {
    DOC_PO_ON_MATCH,
    DOC_PO_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL,
    DOC_PO_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED
} from '@/store/modules/doc-viewer/matching/purchase-order'
import {
    DOC_RECEIPT_ON_MATCH,
    DOC_RECEIPT_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL,
    DOC_RECEIPT_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED
} from '@/store/modules/doc-viewer/matching/supplier-receipt'
import {MatchingExtensionTypes} from '@/types/document-viewer/extension/matching'

export default {
    name: 'DocumentAllocations',
    components: {DocumentMatchingItemsSelectModal},
    props: {
        changeSelection: {
            type: Function,
            required: false,
            default: () => {
            }
        }
    },
    data() {
        return {
            selectedRows: [],
            getRowNodeId: null,
            gridApi: null,
            columnApi: null,
            columnDefs: null,
            defaultColDef: null,
            frameworkComponents: null,

            isLoading: false
        }
    },
    computed: {
        ...mapGetters('doc_viewer', [
            'doc',
            'allocations',
            'getFormFieldValue',
            'matchingSupplier',
            'disabledDocument'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'settings',
            'fields',
            'isSettingsEditMode',
            'getFieldSettingsByPath',
            'getRoundingDecimalsByFieldPath',
            'getThousandSeparatorByFieldPath',
            'supplierReceiptsCheckEnabled',
            'poTotalDeviationPercentAllowed',
            'supplierReceiptsTotalDeviationPercentAllowed',
            'poTotalDeviationCheckRequiredToProcess',
            'supplierReceiptsTotalCheckRequiredToProcess'
        ]),

        ...mapGetters('doc_viewer_metadata', [
            'getFieldMetadata',
            'isTouchedForm'
        ]),

        tableHeight() {
            if (this.allocations.length <= 5) {
                return 4 * 34
            } else {
                return 6 * 34
            }
        },

        itemType() {
            return this.supplierReceiptsCheckEnabled
                ? MatchingExtensionTypes.SUPPLIER_RECEIPT
                : MatchingExtensionTypes.PURCHASE_ORDER
        },

        isSupplierReceipt() {
            return this.itemType === MatchingExtensionTypes.SUPPLIER_RECEIPT
        },

        isPurchaseOrder() {
            return this.itemType === MatchingExtensionTypes.PURCHASE_ORDER
        },

        itemTypeLabel() {
            return this.isPurchaseOrder ? 'purchase order' : 'supplier receipt'
        },

        displayCurrency() {
            return _.get(this.doc, 'currency.symbol')
        },

        docTotal() {
            return forceToNumber(this.doc.total)
        },

        totalAllocatedSum() {
            return this.allocations.reduce((sum, item) => sum + forceToNumber(item.allocated), 0)
        },

        totalOutstandingSum() {
            return Math.max(0, this.docTotal - this.totalAllocatedSum)
        },

        hasTotalAllocatedGreaterThanDocTotal() {
            return this.totalAllocatedSum > this.docTotal
        },

        totalDeviationPercentAllowed() {
            return this.isPurchaseOrder
                ? this.poTotalDeviationPercentAllowed
                : this.supplierReceiptsTotalDeviationPercentAllowed
        },

        totalCheckRequiredToProcess() {
            return this.isPurchaseOrder
                ? this.poTotalDeviationCheckRequiredToProcess
                : this.supplierReceiptsTotalCheckRequiredToProcess
        },

        hasTotalUnallocatedGreaterThanAllowed() {
            if (!this.totalCheckRequiredToProcess) {
                return false
            }
            return 100 * this.totalOutstandingSum / this.docTotal > this.totalDeviationPercentAllowed
        },

        thousandSeparator() {
            return this.getThousandSeparatorByFieldPath('total')
        },

        precision() {
            return this.getRoundingDecimalsByFieldPath('total')
        },

        formattedTotalAllocatedSum() {
            return accounting.formatMoney(this.totalAllocatedSum, {
                symbol: '',
                format: '%v',
                decimal: '.',
                thousand: this.thousandSeparator,
                precision: this.precision
            })
        },

        formattedTotalOutstandingSum() {
            return accounting.formatMoney(this.totalOutstandingSum, {
                symbol: '',
                format: '%v',
                decimal: '.',
                thousand: this.thousandSeparator,
                precision: this.precision
            })
        },

        filteredFields() {
            return _.uniqBy(
                this.fields.filter(f => f.visible && f.section.key === FormSystemSections.ALLOCATIONS),
                'path'
            ).sort((a, b) => a.section.order - b.section.order)
        }
    },
    methods: {
        ...mapActions('doc_viewer', {
            updateField: DOC_FIELD_UPDATE
        }),

        ...mapMutations('doc_viewer', {
            setAllocations: DOC_SET_ALLOCATIONS
        }),

        ...mapMutations('doc_viewer_po', {
            setPoHasTotalAllocatedGreaterThanDocTotal: DOC_PO_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL,
            setPoHasTotalUnallocatedGreaterAllowed: DOC_PO_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED
        }),

        ...mapActions('doc_viewer_po', {
            onMatchPo: DOC_PO_ON_MATCH
        }),

        ...mapMutations('doc_viewer_supplier_receipt', {
            setReceiptHasTotalAllocatedGreaterThanDocTotal: DOC_RECEIPT_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL,
            setReceiptHasTotalUnallocatedGreaterAllowed: DOC_RECEIPT_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED
        }),

        ...mapActions('doc_viewer_supplier_receipt', {
            onMatchReceipt: DOC_RECEIPT_ON_MATCH
        }),

        async onGridReady(params) {
            this.gridApi = params.api
            this.columnApi = params.columnApi

            this.isLoading = true
            await this.setupColumns()
            this.gridApi.setRowData(this.allocations)
            this.gridApi.setFillHandleDirection('y')
            this.isLoading = false
        },

        onCellClicked(event) {
            if (!this.isSettingsEditMode) {
                return
            }
            let {colDef} = event
            const prop = colDef.fieldName
            this.changeSelection({
                section: FormSystemSections.ALLOCATIONS,
                field: `allocations.${prop}`
            })
        },

        async onCellValueChanged(event) {
            let {value, colDef: {field}, rowIndex} = event
            const allocations = _.cloneDeep(this.allocations)
            const allocation = allocations[rowIndex] || {}
            if (field === 'allocated') {
                value = forceToNumber(value)
                if (value < 0) {
                    value = 0
                } else {
                    value = Math.min(value, this.getItemMaxAllowedAllocation(allocation)).toFixed(this.precision)
                }
            }
            _.set(allocation, field, value)

            // Force recalculate extra allocated:
            const totalAllocatedSum = allocations.reduce((sum, item) => sum + forceToNumber(item.allocated), 0)
            const extraAllocated = Math.max(0, totalAllocatedSum - this.docTotal)
            if (extraAllocated > 0) {
                value = (forceToNumber(allocation.allocated) - extraAllocated).toFixed(this.precision)
                _.set(allocation, field, value)
            }
            this.setAllocations(allocations)
        },

        navigateToNextCell(params) {
            const suggestedNextCell = params.nextCellPosition
            const KEY_UP = 38
            const KEY_DOWN = 40
            const noUpOrDownKeyPressed = params.key !== KEY_DOWN && params.key !== KEY_UP
            if (noUpOrDownKeyPressed) {
                return suggestedNextCell
            }
            this.gridApi.forEachNode(node => {
                if (node && suggestedNextCell && node.rowIndex === suggestedNextCell.rowIndex) {
                    node.setSelected(true)
                }
            })
            return suggestedNextCell
        },

        processCellForClipboard(params) {
            if (_.isObject(params.value)) {
                return JSON.stringify(params.value)
            }
            return params.value
        },

        processCellFromClipboard(params) {
            try {
                return _.cloneDeep(JSON.parse(params.value))
            } catch (e) {
                return params.value
            }
        },

        onSelectionChanged() {
            this.selectedRows = this.gridApi.getSelectedRows()
        },

        async onItemAdd() {
            this.$modal.show('document-matching-items', {
                type: this.type,
                onSelect: this.onItemsSelect,
                supplierUuid: _.get(this.matchingSupplier, 'uuid'),
                selectedItems: this.allocations.map(a => a.po)
            })
        },

        async onItemsSelect(items) {
            const allocations = items.map(item => {
                const prevAllocation = this.allocations.find(a => _.get(a, 'po.uuid') === item.uuid)
                if (prevAllocation) {
                    return prevAllocation
                }
                return ({
                    uuid: item.uuid,
                    po: item,
                    allocated: 0
                })
            })
            this.setAllocations(allocations)
            this.autofillAllocationsData()
            await this.$nextTick()
            this.gridApi.ensureIndexVisible(this.allocations.length - 1, 'bottom')
        },

        autofillAllocationsData() {
            _.cloneDeep(this.allocations).forEach((item, idx) => {
                if (Number(item.allocated) > 0) {
                    return
                }
                this.$set(
                    this.allocations[idx],
                    'allocated',
                    Math.min(
                        this.getItemTotalOutstanding(item),
                        this.totalOutstandingSum
                    ).toFixed(this.precision)
                )
            })
        },

        async onRemoveRows() {
            const allocations = _.cloneDeep(this.allocations)
            this.selectedRows.forEach(row => {
                const idx = allocations.findIndex(a => _.get(row, 'po.uuid') === _.get(a, 'po.uuid'))
                if (idx !== -1) {
                    allocations.splice(idx, 1)
                }
            })
            this.setAllocations(allocations)
        },

        getItemLabel(item = {}) {
            return _.get(item, 'po.number', '')
        },

        getItemTotal(item = {}) {
            return forceToNumber(_.get(item, 'po.total', 0))
        },

        getItemTotalAllocatedEarlier(item = {}) {
            return forceToNumber(_.get(item, 'po.allocated', 0))
        },

        getItemMaxAllowedAllocation(item = {}) {
            return this.getItemTotal(item) - this.getItemTotalAllocatedEarlier(item)
        },

        getItemTotalAllocated(item = {}) {
            return forceToNumber(_.get(item, 'allocated', 0))
        },

        getItemTotalOutstanding(item = {}) {
            return Math.max(0, this.getItemMaxAllowedAllocation(item) - this.getItemTotalAllocated(item))
        },

        async setupColumns() {
            this.columnDefs = [{
                name: 'uuid',
                field: 'uuid',
                headerName: _.capitalize(this.itemTypeLabel),
                valueFormatter: ({data}) => this.getItemLabel(data),
                editable: false,
                width: 210
            }, {
                name: 'allocated',
                field: 'allocated',
                headerName: 'Allocated',
                editable: !this.disabledDocument,
                width: 150,
                type: 'rightAligned'
            }, {
                name: 'remaining',
                field: 'remaining',
                headerName: 'Remaining',
                valueFormatter: ({data}) => this.getItemTotalOutstanding(data).toFixed(this.precision),
                editable: !this.disabledDocument,
                width: 150,
                type: 'rightAligned'
            }]
        }
    },
    beforeMount() {
        this.frameworkComponents = {
            tooltipComponent: AgGridCellTooltip
        }
        this.getRowNodeId = data => _.get(data, 'po.uuid')

        this.defaultColDef = {
            width: 90,
            sortable: true,
            filter: true,
            editable: !this.disabledDocument,
            resizable: true,
            tooltipComponent: 'tooltipComponent',
            tooltipValueGetter: p => ({value: p.value}),
            cellClass: params => {
                if (params.colDef.cellRendererFramework) {
                    return 'px-0'
                }
                return 'px-2'
            }
        }
    },
    watch: {
        allocations: {
            handler(allocations, prevAllocations) {
                if (this.gridApi) {
                    this.gridApi.setRowData(this.allocations)
                    this.gridApi.refreshCells({force: true})
                }
                if (allocations && allocations.length > 0 &&
                    (!prevAllocations || prevAllocations.length === 0)
                ) {
                    const item = _.get(allocations[0], 'po')
                    if (item) {
                        if (this.isPurchaseOrder) {
                            this.onMatchPo({purchaseOrder: item})
                        } else {
                            this.onMatchReceipt({deliveryDocket: item})
                        }
                    }
                }
            },
            deep: true
        },
        isLoading(v) {
            if (this.gridApi) {
                if (v) {
                    this.gridApi.showLoadingOverlay()
                } else {
                    this.gridApi.hideOverlay()
                }
            }
        },
        hasTotalAllocatedGreaterThanDocTotal: {
            handler(v) {
                if (this.isPurchaseOrder) {
                    this.setPoHasTotalAllocatedGreaterThanDocTotal(v)
                } else {
                    this.setReceiptHasTotalAllocatedGreaterThanDocTotal(v)
                }
            },
            immediate: true
        },
        hasTotalUnallocatedGreaterThanAllowed: {
            handler(v) {
                if (this.isPurchaseOrder) {
                    this.setPoHasTotalUnallocatedGreaterAllowed(v)
                } else {
                    this.setReceiptHasTotalUnallocatedGreaterAllowed(v)
                }
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss" scoped>
.table-status-bar {
    border: 1px solid #bdc3c7;
    border-top: none;
    padding: 8px 12px;
    line-height: 21px;
}
</style>
